import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Alert, Button, EmptyState, Skeleton } from "@/components/ui";
import { useAuthStore } from "@/stores/auth.store";
import { localized, useLang } from "@/i18n";
import { errorMessage } from "@/lib/api";
import { cn, formatDateTime, formatRelative } from "@/lib/utils";
import {
  type BellItem,
  useBellList,
  useMarkAllRead,
  useMarkRead,
  useUnreadCount,
} from "./api";

/**
 * The in-app bell (INV-100) in the title bar: the unread badge, and a popover
 * listing the user's own items newest first. Opening an item marks it read and
 * follows its deep link; the target screen locates the exact record.
 */
export function NotificationBell() {
  const { t } = useTranslation("notifications");
  const lang = useLang();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount(Boolean(user));
  const list = useBellList(Boolean(user) && open);
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();
  if (!user) return null;

  const count = unread.data ?? 0;
  const items = list.data?.pages.flatMap((p) => p.data) ?? [];
  const label = count ? t("bellUnread", { count }) : t("bell");

  const openItem = (item: BellItem) => {
    if (!item.isRead) markRead.mutate(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          data-unread={count}
          aria-label={label}
          title={label}
          className="relative flex items-center rounded px-1.5 py-0.5 text-slate-300 transition hover:bg-slate-700 hover:text-white
                     max-lg:h-touch max-lg:w-touch max-lg:justify-center max-lg:px-0"
        >
          <Bell className="h-3.5 w-3.5 max-lg:h-5 max-lg:w-5" aria-hidden="true" />
          {count > 0 && (
            <span
              data-testid="notification-bell-count"
              className="ltr-num absolute -top-1 -end-1 min-w-[16px] rounded-full bg-red-600 px-1 text-center font-mono text-[9px] font-bold leading-4 text-white
                         max-lg:top-1 max-lg:end-1"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          data-testid="notification-panel"
          className="z-50 flex max-h-[min(32rem,calc(100dvh-4rem))] w-[22rem] max-w-[calc(100vw-16px)] flex-col overflow-hidden
                     rounded border border-slate-200 bg-white text-slate-800 shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <strong className="text-xs">{t("title")}</strong>
            <Button
              size="sm"
              variant="ghost"
              icon={CheckCheck}
              className="ms-auto"
              data-testid="notification-read-all"
              disabled={!count || markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              {t("markAllRead")}
            </Button>
          </div>
          <div className="touch-scroll flex-1 overflow-y-auto">
            {(list.error || markAll.error || markRead.error) && (
              <div className="p-2">
                <Alert tone="danger" data-testid="notification-error">
                  {errorMessage(list.error ?? markAll.error ?? markRead.error, lang)}
                </Alert>
              </div>
            )}
            {list.isLoading && (
              <div className="space-y-2 p-3">
                <Skeleton h={40} />
                <Skeleton h={40} />
              </div>
            )}
            {!list.isLoading && !list.error && items.length === 0 && (
              <div data-testid="notification-empty" className="p-3">
                <EmptyState title={t("empty")} />
              </div>
            )}
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    data-testid="notification-item"
                    data-notification-id={item.id}
                    data-read={item.isRead}
                    onClick={() => openItem(item)}
                    className={cn(
                      "flex w-full gap-2 px-3 py-2 text-start transition hover:bg-slate-50 max-lg:py-3",
                      !item.isRead && "bg-blue-50/60",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                        item.isRead
                          ? "bg-transparent"
                          : item.severity === "INFO"
                            ? "bg-blue-600"
                            : "bg-amber-500",
                      )}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className={cn("text-xs", item.isRead ? "text-slate-600" : "font-bold text-slate-900")}>
                        {localized(lang, item.titleAr, item.titleEn ?? undefined)}
                      </span>
                      {(item.bodyAr || item.bodyEn) && (
                        <span className="line-clamp-2 text-[11px] text-slate-500">
                          {localized(lang, item.bodyAr ?? "", item.bodyEn ?? undefined)}
                        </span>
                      )}
                      <time
                        className="text-[10px] text-slate-400"
                        dateTime={item.createdAt}
                        title={formatDateTime(item.createdAt, lang)}
                      >
                        {formatRelative(item.createdAt, lang)}
                      </time>
                    </span>
                    {!item.isRead && <span className="sr-only">{t("unread")}</span>}
                  </button>
                </li>
              ))}
            </ul>
            {list.hasNextPage && (
              <div className="p-2 text-center">
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="notification-more"
                  loading={list.isFetchingNextPage}
                  onClick={() => void list.fetchNextPage()}
                >
                  {t("loadMore")}
                </Button>
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
